/** @jsxImportSource @emotion/react */
import { useSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import React, { useEffect } from 'react'
import { getInitialSelectedSubjectId, usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { PageLoader } from 'components/PageLoader'
import { SubjectTabs } from 'components/SubjectsTabs'
import { PlatformSlug } from 'constants/consts'
import { findAllWithSubjectsTabs_findAllWithSubjectsTabs_subjects as Subject } from '__generatedTypes__/findAllWithSubjectsTabs'
import { getQBTitle, isSubjectWithoutQBCategory } from '../utils'
import { NotFound } from '../../../components/NotFound'
import { ListWithCategories } from './ListWithCategories'
import { ListWithoutCategories } from './ListWithoutCategories'
import * as classes from './styles'
import { DescriptionForm } from './DescriptionForm'

export type ListProps = {
  override?: {
    tabIndex: string
    availablePlatforms: PlatformSlug[]
    filterSubjects?: (subject: Subject) => boolean
  }
}

export const List = ({ override }: ListProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const tabIndex = override?.tabIndex ?? searchParams.get('tabIndex')
  const { selectedPlatform, subjects, selectedSubject, selectSubject } = usePlatformsAndSubjects({
    initialSelectedSubjectId: getInitialSelectedSubjectId(subjectId, tabIndex) || null,
    isQuestionBanks: true,
    tabIndex: override ? Number(override.tabIndex) : undefined,
    filterSubjects: override?.filterSubjects,
  })

  useEffect(() => {
    if (!selectedSubject) {
      return
    }

    setSearchParams((prev) => ({
      ...Object.fromEntries(prev.entries()),
      subjectId: selectedSubject.id,
      tabIndex: String(selectedSubject.tabIndex),
    }))
  }, [selectedSubject?.tabId])

  if (!selectedPlatform || !selectedSubject) {
    return <PageLoader />
  }

  if (override && !override.availablePlatforms.includes(selectedPlatform.slug as PlatformSlug)) {
    return <NotFound />
  }

  const isWithoutCategory = isSubjectWithoutQBCategory(selectedSubject)

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        {getQBTitle(false, selectedSubject.tabIndex, true)}
      </Typography>
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />

      <DescriptionForm subjectId={selectedSubject.id} tabIndex={selectedSubject.tabIndex} />
      {isWithoutCategory ? (
        <ListWithoutCategories selectedSubject={selectedSubject} />
      ) : (
        <ListWithCategories selectedSubject={selectedSubject} />
      )}
    </div>
  )
}
